import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ClipboardList,
  Plus,
  Filter,
  Moon,
  Trash2,
  Pencil,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardContent } from "@/components/ui/Card";
import { ActivityForm } from "./ActivityForm";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Activity {
  id: number;
  person_id: number;
  program_id: number | null;
  staff_id: number | null;
  funder_id: number | null;
  site_id: number | null;
  activity_date: string;
  activity_time: string | null;
  duration: number | null;
  zip_code: string | null;
  is_bed_night: number;
  is_hotel_bed_night: number;
  is_residence_bed_night: number;
  is_transitional_housing: number;
  notes: string | null;
  created_at: string;
  // Joined
  program_name?: string;
  staff_first_name?: string;
  staff_last_name?: string;
  funder_name?: string;
  site_name?: string;
}

interface ActivitiesResponse {
  data: Activity[];
  total: number;
  page: number;
  limit: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ActivitiesListProps {
  personId: string;
}

export function ActivitiesList({ personId }: ActivitiesListProps) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);

  const params: Record<string, string> = { page: String(page), limit: "25" };
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const { data, isLoading } = useQuery<ActivitiesResponse>({
    queryKey: ["activities", personId, page, startDate, endDate],
    queryFn: () =>
      api.get<ActivitiesResponse>(`/clients/${personId}/activities`, {
        params,
      }),
    enabled: !!personId,
  });

  const deleteMutation = useMutation({
    mutationFn: (activityId: number) =>
      api.delete(`/clients/${personId}/activities/${activityId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities", personId] });
    },
  });

  function handleEdit(activity: Activity) {
    setEditingActivity(activity);
    setShowForm(true);
  }

  function handleFormClose() {
    setShowForm(false);
    setEditingActivity(null);
  }

  if (showForm) {
    return (
      <ActivityForm
        personId={personId}
        activity={editingActivity}
        onClose={handleFormClose}
      />
    );
  }

  const activities = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 25);

  // Calculate total hours from current page
  const totalHours = activities.reduce(
    (sum, a) => sum + (a.duration ?? 0),
    0,
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Activities</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4" />
            Filters
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => setShowForm(true)}
          >
            <Plus className="h-4 w-4" />
            New Activity
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="flex gap-4">
        <div className="rounded-md border bg-muted/30 px-4 py-2 text-sm">
          <span className="text-muted-foreground">Total records: </span>
          <span className="font-medium">{total}</span>
        </div>
        <div className="rounded-md border bg-muted/30 px-4 py-2 text-sm">
          <span className="text-muted-foreground">Page hours: </span>
          <span className="font-medium">{totalHours.toFixed(1)} hrs</span>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="w-48">
                <Input
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="w-48">
                <Input
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="h-64 animate-pulse rounded-lg border bg-muted" />
      )}

      {/* Empty state */}
      {!isLoading && activities.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <ClipboardList className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No activities recorded.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Activities table */}
      {!isLoading && activities.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Program
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Duration
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Staff
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Funder
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Bed Night
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {activities.map((activity) => {
                  const staffName =
                    activity.staff_first_name && activity.staff_last_name
                      ? `${activity.staff_first_name} ${activity.staff_last_name}`
                      : "-";

                  const bedNightTypes: string[] = [];
                  if (activity.is_bed_night) bedNightTypes.push("Standard");
                  if (activity.is_hotel_bed_night) bedNightTypes.push("Hotel");
                  if (activity.is_residence_bed_night)
                    bedNightTypes.push("Residence");
                  if (activity.is_transitional_housing)
                    bedNightTypes.push("Transitional");

                  return (
                    <tr
                      key={activity.id}
                      className="border-b last:border-0 hover:bg-muted/30"
                    >
                      <td className="px-4 py-3">
                        {new Date(activity.activity_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        {activity.program_name ? (
                          <span className="inline-flex rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                            {activity.program_name}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {activity.duration != null
                          ? `${activity.duration} hrs`
                          : "-"}
                      </td>
                      <td className="px-4 py-3">{staffName}</td>
                      <td className="px-4 py-3">
                        {activity.funder_name ?? "-"}
                      </td>
                      <td className="px-4 py-3">
                        {bedNightTypes.length > 0 ? (
                          <div className="flex items-center gap-1">
                            <Moon className="h-3.5 w-3.5 text-indigo-500" />
                            <span className="text-xs">
                              {bedNightTypes.join(", ")}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(activity)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              if (
                                confirm(
                                  "Are you sure you want to delete this activity?",
                                )
                              ) {
                                deleteMutation.mutate(activity.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {activities.length} of {total} activities
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
